import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { FaRoute } from "react-icons/fa";
import { IoLocation } from "react-icons/io5";
import Handler from "../../Helpers/Handler";
import useGoogleMaps from "../../Helpers/useGoogleMaps";
import { formatDateTime } from "../../Helpers/Utils";
import { DetailCard, Skeleton } from "../../Components/Needed";
import { MAP_OPTIONS, MAP_STYLE, STATUS_META, TASK_COLOR, agoText, durationText, fitTo, metresText, pinIcon, toLatLng, toLocalInput } from "../StaffTracking/trackingShared";

const REFRESH_MS = 10000;

const Row = ({ label, children }) => (
    <div className="info-field">
        <div className="info-field-label">{label}</div>
        <div>{children || "-"}</div>
    </div>
);

// Where the executive is / was on one delivery or pick-up: live position while the task is in
// progress, the route afterwards, and the start/finish proof (how far from the address).
const TaskTrackingPanel = ({ task }) => {
    const show = ["progress", "completed"].includes(task?.status) && !!task?.staffId;
    const { isLoaded, loadError } = useGoogleMaps();
    const mapRef = useRef(null);
    const fitted = useRef(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [now, setNow] = useState(Date.now());

    const load = useCallback(async () => {
        const response = await Handler({ method: "GET", url: `/admin/location/task/${task.taskId}` });
        if (response?.success) setData(response?.data?.data || null);
        setLoading(false);
    }, [task?.taskId]);

    useEffect(() => {
        if (!show) return undefined;
        fitted.current = false;
        load();
        if (task.status !== "progress") return undefined;
        const timer = setInterval(() => { setNow(Date.now()); if (!document.hidden) load(); }, REFRESH_MS);
        return () => clearInterval(timer);
    }, [show, task?.status, load]);

    const route = useMemo(() => (data?.trail?.points || []).map(toLatLng).filter(Boolean), [data]);
    const destination = useMemo(() => (data?.destination ? toLatLng(data.destination) : null), [data]);
    const current = useMemo(() => (data?.current ? toLatLng(data.current) : null), [data]);
    const startSpot = useMemo(() => (data?.proof?.start ? { lat: data.proof.start.latitude, lng: data.proof.start.longitude } : null), [data]);
    const endSpot = useMemo(() => (data?.proof?.end ? { lat: data.proof.end.latitude, lng: data.proof.end.longitude } : null), [data]);
    const hasMapData = route.length > 0 || !!current || !!destination;

    useEffect(() => {
        if (!isLoaded || !mapRef.current || fitted.current || !hasMapData) return;
        fitted.current = true;
        fitTo(mapRef.current, window.google, [...route, current, destination, startSpot, endSpot]);
    }, [isLoaded, hasMapData, route, current, destination, startSpot, endSpot]);

    if (!show) return null;

    const farLimit = data?.proof?.farLimitM || 300;
    const proofText = (spot) => {
        if (!spot) return "Not recorded";
        if (!Number.isFinite(spot.distanceM)) return `Recorded ${formatDateTime(spot.at)} (no address on file to compare)`;
        const far = spot.distanceM > farLimit;
        return (
            <span>
                {metresText(spot.distanceM)} from the address
                {far ? <span className="custom-badge badge-warning ms-2">Far from address</span> : <span className="custom-badge badge-active ms-2">At address</span>}
            </span>
        );
    };

    const state = data?.status === "completed" ? "Completed" : data?.arrivedAt ? `Arrived at ${formatDateTime(data.arrivedAt)}` : "On the way";
    const live = data?.current ? STATUS_META[data.current.status] : null;
    const routeLink = (() => {
        if (!data?.staff) return "";
        const shift = (time, minutes) => toLocalInput(new Date(new Date(time).getTime() + minutes * 60000));
        const query = new URLSearchParams({ taskId: task.taskId });
        if (task.startTime) query.set("from", shift(task.startTime, -5));
        if (task.endTime) query.set("to", shift(task.endTime, 5));
        return `/staff-tracking/route/${data.staff.staffId}?${query.toString()}`;
    })();

    return (
        <DetailCard title={task.status === "progress" ? "Live tracking" : "Route and proof"} icon={<IoLocation />}>
            {loading ? <Skeleton height={260} /> : !data ? <p className="text-muted mb-0">Tracking details could not be loaded.</p> : (
                <>
                    <div className="info-grid px-1 mb-3">
                        <Row label="Executive">{data.staff ? <Link to={`/staff/${data.staff.staffId}`}>{data.staff.name}</Link> : "-"}</Row>
                        <Row label="State">{state}</Row>
                        {data.current ? (
                            <Row label="Last location">
                                {agoText(data.current.at, now)}
                                {live ? <span className={`custom-badge ${live.badge} ms-2`}>{live.label}</span> : null}
                                {Number.isFinite(data.current.distanceToAddressM) ? ` · ${metresText(data.current.distanceToAddressM)} from the address` : ""}
                            </Row>
                        ) : null}
                        <Row label="Distance travelled">{route.length > 1 ? `${data.trail.distanceKm} km` : ""}</Row>
                        {data.startTime && data.endTime ? <Row label="Time on task">{durationText(data.startTime, data.endTime)}</Row> : null}
                        <Row label="At start">{proofText(data.proof?.start)}</Row>
                        <Row label="At finish">{data.status === "completed" ? proofText(data.proof?.end) : "When completed"}</Row>
                    </div>
                    {task.status === "progress" && !data.current ? (
                        <div className="alert alert-warning py-2 small">No recent location from this executive. Their phone may be offline, out of battery or have sharing switched off.</div>
                    ) : null}
                    {hasMapData ? (
                        <div className="st-task-map">
                            {loadError ? <div className="p-3 text-danger">The map could not be loaded.</div> : !isLoaded ? <Skeleton height="100%" /> : (
                                <GoogleMap mapContainerStyle={MAP_STYLE} center={destination || current || route[0]} zoom={14} options={MAP_OPTIONS} onLoad={(map) => { mapRef.current = map; }}>
                                    {route.length > 1 ? <Polyline path={route} options={{ strokeColor: TASK_COLOR, strokeOpacity: 0.85, strokeWeight: 5 }} /> : null}
                                    {destination ? <Marker position={destination} title="Delivery / pick-up address" label={{ text: "A", color: "#fff", fontWeight: "700" }} /> : null}
                                    {startSpot ? <Marker position={startSpot} title="Where the task was started" label={{ text: "S", color: "#fff", fontWeight: "700" }} /> : null}
                                    {endSpot ? <Marker position={endSpot} title="Where the task was completed" label={{ text: "E", color: "#fff", fontWeight: "700" }} /> : null}
                                    {current ? <Marker position={current} zIndex={10} title={data.staff?.name} icon={pinIcon(window.google, { color: (live || STATUS_META.live).color, ring: true, scale: 12 })} /> : null}
                                </GoogleMap>
                            )}
                        </div>
                    ) : <p className="text-muted mb-0">No location was recorded for this task.</p>}
                    {routeLink ? <Link to={routeLink} className="d-inline-flex align-items-center gap-2 mt-3 small"><FaRoute /> Open full route with playback</Link> : null}
                </>
            )}
        </DetailCard>
    );
};

export default TaskTrackingPanel;
